import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import { API, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { setUser } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await API.post("/auth/login", { email, password });
      setUser(data);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center pt-20 px-6" data-testid="login-page">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <div className="flex flex-col items-center mb-10">
          <Logo size={44} />
          <h1 className="font-display text-3xl text-white mt-6 tracking-tight">Studio Access</h1>
          <p className="text-xs tracking-[0.2em] uppercase text-zinc-500 mt-3">Owner sign-in</p>
        </div>
        <form onSubmit={submit} className="bg-[#0A0A0A] border border-white/10 p-8 md:p-10 space-y-6" data-testid="login-form">
          <div>
            <label htmlFor="login-email" className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Email</label>
            <input
              id="login-email"
              data-testid="login-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-black border border-white/15 text-white text-sm px-4 py-4 focus:border-[#D4AF37] focus:outline-none transition-colors duration-300"
              placeholder="you@studio.com"
            />
          </div>
          <div>
            <label htmlFor="login-password" className="block text-xs tracking-[0.2em] uppercase text-zinc-500 mb-3">Password</label>
            <input
              id="login-password"
              data-testid="login-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-black border border-white/15 text-white text-sm px-4 py-4 focus:border-[#D4AF37] focus:outline-none transition-colors duration-300"
              placeholder="••••••••"
            />
          </div>
          {error && (
            <p className="text-sm text-red-400" data-testid="login-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-button"
            className="w-full inline-flex items-center justify-center gap-3 bg-white text-black text-xs tracking-[0.25em] uppercase font-semibold px-10 py-5 hover:bg-[#D4AF37] transition-colors duration-300 disabled:opacity-60"
          >
            {loading && <Loader2 className="animate-spin" size={15} />}
            Sign in
          </button>
        </form>
      </motion.div>
    </div>
  );
}
